import * as React from 'react';
import { IBookingType } from './AppointmentComponent';
import KalenderTooltipHandlerComponent from '../../KalenderTooltipHandlerComponent';
import { makeStyles } from 'tss-react/mui';
import { useContext } from 'react';
import { SchedulerContext } from '../../SchedulerContext';
import {
  useIsExtraSmallScreen,
  useIsSmallScreen,
} from '../../../../../../../../Hooks/useFullscreenView';
// Använd tillfälligt patchad version av gamla kalenderkomponenten som nu använder LegacyGrid för att den annars inte fungerar med React 19
import { Content } from '../../PatchedLegacyScheduler/appointment-tooltip/content';

export declare type FormatterFn = (
  nextDate: Date | number | string,
  nextOptions: {
    year?: string;
    month?: string;
    day?: string;
    weekday?: string;
    hour?: string;
    minute?: string;
  }
) => string;

export interface IModelProps {
  /** The start date. */
  startDate: Date | number | string;
  /** The end date. */
  endDate?: Date | number | string;
  /** The title. */
  title?: string;
  /** The all day flag. */
  allDay?: boolean;
  /** The identifier. */
  id?: number | string;
  /** Specifies the appointment recurrence rule. */
  rRule?: string | undefined;
  /** Specifies dates excluded from recurrence. */
  exDate?: string | undefined;
  /** Any other properties. */
  [propertyName: string]: any;
}

interface IProps {
  /** The appointment’s displayed metadata. */
  appointmentData?: IModelProps;
  /** A function that formats dates according to the locale. */
  formatDate: FormatterFn;
  /** A component that renders an icon that indicates a recurring appointment. */
  recurringIconComponent: React.ComponentType<object>;
  /** A React node that renders custom elements in the tooltip. */
  children?: React.ReactNode;
}

// TODO jss-to-tss-react codemod: Unable to handle style definition reliably. Unsupported arrow function syntax.
// Arrow function has parameter type of Identifier instead of ObjectPattern (for example `(props) => ({...})` instead of `({color}) => ({...})`).
const useStyles = makeStyles<{ bokningsbar: any; type: any }>()(
  (theme, props) => ({
    appointmentTooltip: {
      '& > :first-of-type': {
        '& > :first-of-type': {
          '& > :first-of-type': {},
        },
      },
      '& .Content-lens': {
        color: props.bokningsbar
          ? (theme.customProperties.AktivitetColors.bokningsbar as unknown as Record<string, string | undefined>)[(props.type ?? '') as string]
          : (theme.customProperties.AktivitetColors as unknown as Record<string, string | undefined>)[(props.type ?? '') as string],
      },
      '& .Content-resourceContainer': {
        display: 'none',
      },
    },
    tooltipContent: {
      '& > :first-of-type': {
        '& > :nth-of-type(2)': {
          '& > :first-of-type': {
            '& > :first-of-type': {
              maxWidth: '85%',
              overflowWrap: 'break-word',
            },
          },
        },
      },
    },
    titleMaxWidth: {
      '& > :first-of-type': {
        '& > :nth-of-type(2)': {
          '& > :first-of-type': {
            '& > :first-of-type': {
              maxWidth: '60%',
            },
          },
        },
      },
    },
  })
);

export const CustomTooltipComponent = (props: IProps) => {
  let data = props.appointmentData as IBookingType;
  let color = data?.aktivityType?.toLowerCase();
  const schedulerContext = useContext(SchedulerContext);
  const isSmallScreen = useIsSmallScreen();
  const isExtraSmallScreen = useIsExtraSmallScreen();

  const { classes, cx } = useStyles({
    type: color,
    bokningsbar: false,
  });

  let isStalltid: boolean = false;
  if (
    props.appointmentData?.aktStartDate !== props.appointmentData?.startDate ||
    props.appointmentData?.aktEndDate !== props.appointmentData?.endDate
  ) {
    isStalltid = true;
  }

  const mouseLeave = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (!isSmallScreen || !isExtraSmallScreen) {
      const relatedTarget = e?.relatedTarget as HTMLElement | null;
      if (
        relatedTarget?.className &&
        typeof relatedTarget?.className === 'string'
      ) {
        if (relatedTarget?.className?.startsWith('MuiBackdrop-root')) {
          schedulerContext.tooltipOnHover(true);
        }
      }
    }
  };

  return (
    <Content
      appointmentResources={[]}
      {...props}
      formatDate={props.formatDate}
      recurringIconComponent={props.recurringIconComponent}
      className={cx(classes.appointmentTooltip, classes.tooltipContent, {
        [classes.titleMaxWidth]: isSmallScreen,
      })}
      appointmentData={{
        ...props.appointmentData,
        startDate: isStalltid
          ? props.appointmentData?.aktStartDate
          : props.appointmentData?.startDate,
        endDate: isStalltid
          ? props.appointmentData?.aktEndDate
          : props.appointmentData?.endDate,
        location: undefined,
      }}
      onMouseLeave={mouseLeave}
      data-testid={'tooltipContent'}
    >
      {KalenderTooltipHandlerComponent(props.appointmentData as IBookingType)}
    </Content>
  );
};
